import { joinRoom, getRelaySockets } from '@trystero-p2p/ws-relay'
import { PRESENCE_HEARTBEAT_MS, PRESENCE_THROTTLE_MS, PRESENCE_TTL_MS, busy, cleanPresence } from './presence.js'

const STUN_ONLY = [{ urls: 'stun:stun.cloudflare.com:3478' }]

export async function connect({ id, secret, getSnapshot, onSnapshot, onChange, onPresence }) {
  const ice = await fetch('/api/ice')
    .then((r) => r.json())
    .catch(() => ({ iceServers: STUN_ONLY, turn: false }))

  const relayUrl = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/relay/${id}`
  const status = { relay: 'connecting', turn: ice.turn, error: null, peers: {} }

  const room = joinRoom(
    {
      appId: 'groupchat.moreutils.com',
      password: secret, // encrypts SDP so the relay can't read session details
      relayConfig: { urls: [relayUrl] },
      rtcConfig: { iceServers: ice.iceServers }, // replaces Trystero's default Google STUN
    },
    id,
    {
      onJoinError: ({ error }) => {
        status.error = error
        onChange(status)
      },
    },
  )

  const sync = room.makeAction('sync')
  const live = room.makeAction('presence')
  const sendLive = (p, target = null) => live.send(p, { target }).catch(() => {})

  // Everyone else's presence, keyed by peer.
  const others = new Map()
  let mine = {}
  let timer = null
  let dirty = false
  const flush = () => {
    dirty = false
    sendLive(mine)
  }
  const ticker = setInterval(() => {
    if (busy(mine)) flush()
    const cutoff = Date.now() - PRESENCE_TTL_MS
    let changed = false
    for (const [peer, e] of others) if (e.at < cutoff) changed = others.delete(peer) || changed
    if (changed) onPresence?.(others)
  }, PRESENCE_HEARTBEAT_MS)

  room.onPeerJoin = (peerId) => {
    status.peers[peerId] = null
    onChange(status)
    sync.send(getSnapshot(), { target: peerId })
    if (busy(mine)) sendLive(mine, peerId)
  }

  room.onPeerLeave = (peerId) => {
    delete status.peers[peerId]
    onChange(status)
    if (others.delete(peerId)) onPresence?.(others)
  }

  live.onMessage = (p, { peerId }) => {
    others.set(peerId, { ...cleanPresence(p), at: Date.now() })
    onPresence?.(others)
  }

  sync.onMessage = (snap, { peerId }) => {
    status.peers[peerId] = typeof snap?.from === 'string' ? snap.from : null
    onChange(status)
    // Forward news to everyone else so the roster converges even if two phones never manage a
    // direct connection. Merges are idempotent, so this stops once nothing changes.
    if (onSnapshot(snap)) {
      const others = Object.keys(room.getPeers()).filter((p) => p !== peerId)
      if (others.length) sync.send(getSnapshot(), { target: others })
    }
  }

  const checkRelay = () => {
    const ws = getRelaySockets()[relayUrl]
    const next = ws?.readyState === 1 ? 'open' : 'connecting'
    if (next !== status.relay) {
      status.relay = next
      onChange(status)
    }
  }
  const relayPoll = setInterval(checkRelay, 500)
  checkRelay()

  return {
    status,
    broadcast: () => sync.send(getSnapshot()),
    // Replace what we're broadcasting (always includes `from`). The first change goes out at once,
    // the rest at most every PRESENCE_THROTTLE_MS.
    setPresence(next) {
      if (JSON.stringify(next) === JSON.stringify(mine)) return
      mine = next
      if (timer) return void (dirty = true)
      flush()
      timer = setTimeout(() => {
        timer = null
        if (dirty) flush()
      }, PRESENCE_THROTTLE_MS)
    },
    leave: () => {
      clearInterval(relayPoll)
      clearInterval(ticker)
      clearTimeout(timer)
      return room.leave()
    },
  }
}
